import type { FleetVehicle } from '@/lib/api/fleet-api'
import { m } from '@/lib/paraglide/messages'
import { getLocale } from '@/lib/paraglide/runtime'

// Shared fleet formatting so the vehicle list, the camera-wall cards and any
// future fleet surface read the same status/motion/distance/battery text.
// motion_status: 0 = running, 1 = stopped, 2 = lost GPS.

export type FleetStatusFilter = 'all' | 'moving' | 'stopped' | 'offline'

// A vehicle with no GPS device was never configured — distinct from a running
// device whose signal dropped — but for filtering both fall under "offline".
export function isFleetOffline(v: FleetVehicle): boolean {
    return (
        !v.gps ||
        Boolean(v.gps_error) ||
        v.disabled ||
        v.gps.motion_status === 2
    )
}

export function matchesFleetFilter(
    v: FleetVehicle,
    filter: FleetStatusFilter,
): boolean {
    if (filter === 'all') return true
    if (filter === 'offline') return isFleetOffline(v)
    if (isFleetOffline(v)) return false
    if (filter === 'moving') return v.gps!.motion_status === 0
    if (filter === 'stopped') return v.gps!.motion_status === 1
    return true
}

export function motionLabel(v: FleetVehicle): string {
    if (v.gps_error === 'no_gps') return m.fleet_vehicle_no_gps()
    if (!v.gps || v.gps_error) return m.fleet_filter_offline()
    switch (v.gps.motion_status) {
        case 0:
            return m.fleet_motion_running({ speed: Math.round(v.gps.speed) })
        case 1:
            return m.fleet_motion_stopped()
        default:
            return m.fleet_filter_offline()
    }
}

// Tailwind bg-* class for the status dot. Mirrors the map marker colours.
export function statusDotClass(v: FleetVehicle): string {
    if (!v.gps || v.gps_error || v.disabled) return 'bg-gray-400'
    switch (v.gps.motion_status) {
        case 0:
            return 'bg-green-500'
        case 1:
            return 'bg-red-500'
        default:
            return 'bg-gray-400'
    }
}

// Evaluate at call time so a locale change is reflected without re-importing.
function relativeFormatter() {
    return new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' })
}

export function relativeTime(dateStr: string): string {
    try {
        const rtf = relativeFormatter()
        const diff = (new Date(dateStr).getTime() - Date.now()) / 1000
        if (Math.abs(diff) < 60) return rtf.format(Math.round(diff), 'second')
        if (Math.abs(diff) < 3600)
            return rtf.format(Math.round(diff / 60), 'minute')
        if (Math.abs(diff) < 86400)
            return rtf.format(Math.round(diff / 3600), 'hour')
        return rtf.format(Math.round(diff / 86400), 'day')
    } catch {
        return ''
    }
}

export function lastUpdateText(v: FleetVehicle): string {
    if (!v.gps?.update_time) return '—'
    return relativeTime(v.gps.update_time) || '—'
}

// Offline covers lost-GPS (motion_status 2) and disabled too — a vehicle the
// badge shows as "Offline" must not also show a live-looking speed/distance.
export function speedText(v: FleetVehicle): string {
    if (isFleetOffline(v)) return '—'
    return `${Math.round(v.gps!.speed)} km/h`
}

// Same unit handling as fleet-vehicle-detail / vehicle-gps-card: km with one
// decimal, percent with none.
export function todayDistanceText(v: FleetVehicle): string {
    if (isFleetOffline(v)) return '—'
    return `${v.gps!.today_distance.toFixed(1)} km`
}

export function batteryText(v: FleetVehicle): string {
    const b = v.gps?.battery_percent
    return b == null ? '—' : `${b.toFixed(0)}%`
}

export function addressText(v: FleetVehicle): string {
    if (v.gps?.address) return v.gps.address
    if (v.gps_error === 'no_gps') return m.fleet_vehicle_no_gps()
    return m.tracking_no_gps_data()
}
